import { DomainFault, type DomainModule } from './contracts';
import type { Snapshot } from './types';
export class DomainRegistry {
  private modules = new Map<string, DomainModule>();
  register(module: DomainModule) {
    if (!/^[a-z][a-z0-9-]*$/.test(module.namespace))
      throw new DomainFault('VALIDATION', '领域命名空间无效');
    const existing = this.modules.get(module.namespace);
    if (existing === module) return;
    if (existing)
      throw new DomainFault('CONFLICT', `重复领域注册：${module.namespace}`);
    const pages = this.all().flatMap((m) => m.pages || []);
    const own = new Set<string>();
    for (const page of module.pages || []) {
      if (
        own.has(page.id) ||
        pages.some((p) => p.id === page.id || p.path === page.path)
      )
        throw new DomainFault('CONFLICT', `页面注册冲突：${page.id}`);
      own.add(page.id);
    }
    this.modules.set(module.namespace, module);
  }
  get(name: string) {
    return this.modules.get(name);
  }
  all() {
    return Array.from(this.modules.values());
  }
  resolve(action: string) {
    const [namespace, ...parts] = action.split('.');
    const module = this.get(namespace);
    const command = module?.commands[parts.join('.')];
    return module && command ? { module, command } : undefined;
  }
  migrate(state: Snapshot) {
    if (state.version !== 1)
      throw new DomainFault(
        'VERSION',
        '存储主版本不兼容，请保留数据并联系维护人员。',
      );
    state.domainVersions ||= {};
    state.domainData ||= {};
    for (const domain of this.all()) {
      const current = state.domainVersions[domain.namespace] || 0;
      if (current > domain.version)
        throw new DomainFault(
          'VERSION',
          `领域${domain.namespace}版本过新，请升级应用。`,
        );
      for (const migration of [...(domain.migrations || [])].sort(
        (a, b) => a.version - b.version,
      )) {
        if (
          migration.version > current &&
          migration.version <= domain.version
        ) {
          if (
            migration.version !==
            (state.domainVersions[domain.namespace] || 0) + 1
          )
            throw new DomainFault(
              'VERSION',
              `领域${domain.namespace}迁移不连续`,
            );
          migration.up(state);
          state.domainVersions[domain.namespace] = migration.version;
        }
      }
      if ((state.domainVersions[domain.namespace] || 0) !== domain.version)
        throw new DomainFault('VERSION', `领域${domain.namespace}缺少迁移。`);
    }
    return state;
  }
}
const registry = new DomainRegistry();
export const registerDomain = (module: DomainModule) =>
  registry.register(module);
export const registeredDomains = () => registry.all();
export const getDomain = (name: string) => registry.get(name);
export const resolveDomainCommand = (action: string) =>
  registry.resolve(action);
export const migrateDomains = (state: Snapshot) => registry.migrate(state);
